import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, Link } from 'react-router-dom';
import { registerAdmin } from '../../api/authApi';
import CustomAlertModal from '../../components/common/CustomAlertModal';
import { FaEye, FaEyeSlash } from 'react-icons/fa';

const AdminRegisterPage = () => {
  const { register, handleSubmit, watch, formState: { errors } } = useForm();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [serverError, setServerError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [alertConfig, setAlertConfig] = useState({ show: false, message: '', onConfirm: null });

  const passwordValue = watch('password');

  const showCustomAlert = (message, onConfirm = null) => {
    setAlertConfig({ show: true, message, onConfirm });
  };

  const hideCustomAlert = () => {
    const { onConfirm } = alertConfig;
    setAlertConfig({ show: false, message: '', onConfirm: null });
    if (onConfirm) onConfirm();
  };

  const onSubmit = async (data) => {
    setIsLoading(true);
    setServerError('');
    try {
      await registerAdmin({
        fullName: data.fullName,
        email: data.email,
        password: data.password,
        confirmPassword: data.confirmPassword
      });
      // show success message then redirect to login
      showCustomAlert('Account created successfully. Please sign in.', () => {
        navigate('/admin/login');
      });
    } catch (err) {
      const respData = err.response?.data;
      let message = respData?.message || err.message || 'Registration failed';
      if (Array.isArray(respData?.data) && respData.data.length > 0) {
        message = respData.data.map((d) => d.msg).join(', ');
      }
      setServerError(message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="vh-100 d-flex justify-content-center align-items-center bg-light" style={{ backgroundImage: 'radial-gradient(#e1dfd5 1px, transparent 1px)', backgroundSize: '20px 20px' }}>
      <div className="card custom-card border-0 shadow-lg" style={{ width: '100%', maxWidth: '600px', borderRadius: '16px' }}>
        <div className="card-body p-5">
          <div className="text-center mb-4">
            <h4 className="fw-bold text-dark mb-1">Create Admin Account</h4>
            <p className="text-muted small">Register a new admin account</p>
          </div>

          {serverError && (
            <div className="alert alert-danger py-2 small mb-3" role="alert">{serverError}</div>
          )}

          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="mb-3">
              <label className="form-label fw-semibold">Full Name</label>
              <input type="text" className={`form-control p-3 bg-light border-0 ${errors.fullName ? 'is-invalid' : ''}`} {...register('fullName', { required: 'Full name is required' })} />
              {errors.fullName && <div className="invalid-feedback">{errors.fullName.message}</div>}
            </div>

            <div className="mb-3">
              <label className="form-label fw-semibold">Email Address</label>
              <input type="email" className={`form-control p-3 bg-light border-0 ${errors.email ? 'is-invalid' : ''}`} {...register('email', { required: 'Email is required' })} />
              {errors.email && <div className="invalid-feedback">{errors.email.message}</div>}
            </div>

            <div className="mb-3">
              <label className="form-label fw-semibold">Password</label>
              <div className="position-relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  className={`form-control p-3 bg-light border-0 pe-5 ${errors.password ? 'is-invalid' : ''}`}
                  placeholder="At least 8 characters"
                  {...register('password', {
                    required: 'Password is required',
                    minLength: { value: 8, message: 'Password must be at least 8 characters' }
                  })}
                />
                <button
                  type="button"
                  className="btn btn-link text-muted position-absolute end-0 top-50 translate-middle-y text-decoration-none"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <FaEyeSlash /> : <FaEye />}
                </button>
              </div>
              {errors.password && <div className="text-danger small mt-1">{errors.password.message}</div>}
            </div>

            <div className="mb-3">
              <label className="form-label fw-semibold">Confirm Password</label>
              <div className="position-relative">
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  className={`form-control p-3 bg-light border-0 pe-5 ${errors.confirmPassword ? 'is-invalid' : ''}`}
                  placeholder="Re-enter password"
                  {...register('confirmPassword', {
                    required: 'Please confirm password',
                    validate: (val) => val === passwordValue || 'Passwords do not match'
                  })}
                />
                <button
                  type="button"
                  className="btn btn-link text-muted position-absolute end-0 top-50 translate-middle-y text-decoration-none"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
                </button>
              </div>
              {errors.confirmPassword && <div className="text-danger small mt-1">{errors.confirmPassword.message}</div>}
            </div>

            <div className="d-flex gap-2">
              <button type="submit" className="btn btn-primary-custom p-3 fw-bold rounded-pill" disabled={isLoading}>
                {isLoading ? 'Creating...' : 'Create Account'}
              </button>
              <Link to="/admin/login" className="btn btn-outline-secondary p-3 fw-semibold rounded-pill">Back to Login</Link>
            </div>
          </form>
        </div>
      </div>
      <CustomAlertModal show={alertConfig.show} message={alertConfig.message} onHide={hideCustomAlert} />
    </div>
  );
};

export default AdminRegisterPage;
